import { faDollarSign } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ACTION } from "actions/paymentIntentActions";
import {
  COMMON_NAMESPACE,
  PAYMENT_AMOUNT_MODAL_NAMESPACE,
} from "constants/i18n-namespaces";
import { Button, RadioInput, RadioRowGroup } from "kubra-ux-forge";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getPaymentIntent } from "selectors/paymentIntent";

export interface IPartialPaymentAmount {
  fullBalanceAmount: number;
  allowPartialPayment: boolean | null;
  currentPaymentAmount?: number;
  updateCallBack: (amount: number) => void;
}

export const PartialPaymentAmount = (props: IPartialPaymentAmount) => {
  const {
    fullBalanceAmount,
    allowPartialPayment,
    currentPaymentAmount,
    updateCallBack,
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation(PAYMENT_AMOUNT_MODAL_NAMESPACE);
  const paymentIntentState = useSelector(getPaymentIntent);

  const amountToCheck =
    currentPaymentAmount ?? paymentIntentState.paymentAmount;
  const hasAmountToCheck =
    typeof amountToCheck === "number" && Number.isFinite(amountToCheck);
  const isOtherAmount =
    !!allowPartialPayment &&
    hasAmountToCheck &&
    amountToCheck !== fullBalanceAmount;

  const [fullBalanceChecked, setFullBalanceChecked] = useState(!isOtherAmount);
  const [otherPaymentAmount, setOtherPaymentAmount] = useState<
    number | undefined
  >(isOtherAmount ? amountToCheck : undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const otherPaymentAmountErrorId = "partial-payment-amount-other-error";

  const validateOtherPaymentAmount = () => {
    if (!otherPaymentAmount) {
      setErrorMessage(t("other-input-required"));
      return false;
    }
    setErrorMessage(undefined);
    return true;
  };

  const handleBalanceChecked = () => {
    setFullBalanceChecked(true);
  };

  const handleBalanceUnchecked = () => {
    setFullBalanceChecked(false);
  };

  useEffect(() => {
    const amountInput = document.querySelector(
      "[data-cy='radio-row-input-number-radio-row-Other']",
    );

    if (!amountInput) {
      return;
    }

    amountInput.setAttribute("aria-labelledby", "radio-row-label-radio-row-Other");

    if (!errorMessage) {
      amountInput.removeAttribute("aria-describedby");
      return;
    }

    amountInput.setAttribute("aria-describedby", otherPaymentAmountErrorId);

    const errorElement = document.querySelector(
      "[data-cy='radio-row-error-radio-row-Other']",
    );
    if (errorElement) {
      errorElement.setAttribute("id", otherPaymentAmountErrorId);
    }
  }, [errorMessage]);

  useEffect(() => {
    const fullBalanceInput = document.querySelector(
      "[data-cy='radio-row-input-number-radio-row-Full balance']"
    );

    if (!fullBalanceInput) {
      return;
    }

    fullBalanceInput.setAttribute(
      "aria-labelledby",
      "radio-row-label-radio-row-Full balance"
    );
  }, []);

  const handleSubmit = () => {
    if (fullBalanceChecked) {
      dispatch({ type: ACTION.ADD_PAYMENT_AMOUNT, payload: fullBalanceAmount });
      updateCallBack(fullBalanceAmount);
    } else {
      if (validateOtherPaymentAmount() && otherPaymentAmount) {
        dispatch({
          type: ACTION.ADD_PAYMENT_AMOUNT,
          payload: otherPaymentAmount,
        });
        updateCallBack(otherPaymentAmount);
      }
    }
  };

  return (
    // @ts-ignore
    <div className="balance-amount-frame-container">
      <div className="balance-amount-frame">
        <div className="ez-pay-balance-frame-small">
          {!allowPartialPayment && (
            <RadioInput
              selected={fullBalanceChecked}
              label={t("full-balance")}
              icon={<FontAwesomeIcon icon={faDollarSign}></FontAwesomeIcon>}
              onSelect={handleBalanceChecked}
              data-cy="radio-full-balance"
              value={fullBalanceAmount}
              onChange={() => undefined}
              disableInput
            />
          )}
          {allowPartialPayment && (
            <RadioRowGroup ariaLabel={t("payment-amount-options")}>
              <RadioInput
                selected={fullBalanceChecked}
                label={t("full-balance")}
                icon={<FontAwesomeIcon icon={faDollarSign}></FontAwesomeIcon>}
                onSelect={handleBalanceChecked}
                id="radio-full-balance"
                data-cy="radio-full-balance"
                value={fullBalanceAmount}
                onChange={() => undefined}
                disableInput
              />
              <RadioInput
                selected={!fullBalanceChecked}
                label={t("other")}
                icon={<FontAwesomeIcon icon={faDollarSign}></FontAwesomeIcon>}
                onSelect={handleBalanceUnchecked}
                id="radio-row-Other"
                value={otherPaymentAmount ?? ""}
                errorMessage={errorMessage}
                onChange={(value: number | undefined) => {
                  if (typeof value === "number" && !isNaN(value)) {
                    setOtherPaymentAmount(value);
                  } else {
                    setOtherPaymentAmount(undefined);
                  }
                }}
              />
            </RadioRowGroup>
          )}
        </div>
        <Button
          type="submit"
          className="ez-pay-balance-done"
          title="continue button"
          data-cy="button-continue"
          onClick={() => handleSubmit()}
        >
          {t("done", { ns: COMMON_NAMESPACE })}
        </Button>
      </div>
    </div>
  );
};
